import { Color, Spacing } from '@persona-id/design-tokens';
import { StarFillIcon } from '@persona-id/design-tokens/icons';
import { intersection, some, uniq } from 'lodash/fp';
import { DateTime } from 'luxon';
import { MouseEvent, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import Pagination from 'dashboard/components/Pagination';
import { ADMIN_ROLE_NAME } from 'dashboard/components/PermissionsTable/constants';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import TableControls, { FilterConfig } from 'dashboard/components-lib/TableControls';
import FormCreateRole, { FormCreateRoleData } from 'dashboard/forms/FormCreateRole';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { Environment } from 'dashboard/models/environment';
import { Role } from 'dashboard/models/role';
import { User } from 'dashboard/models/user';
import { RolesFilterParams } from 'dashboard/redux/actions/roles';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import { HStack } from 'design-system/components/Stack';
import Sticker from 'design-system/components/Sticker';
import Table, {
  TableBody,
  TableCell,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import useToggle from 'lib/hooks/useToggle';
import { PaginationParams } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

interface DashboardRolesViewProps {
  currentUser: User;
  environments: Environment[];
  filters: RolesFilterParams;
  handleCreateRole: (values: FormCreateRoleData) => Promise<void>;
  isLoading: boolean;
  onOmniSearch: (omniQuery: string) => void;
  onPageLinkClick: (pageNumber: number) => void;
  onPageSizeClick: (pageSize: number) => void;
  pageSize: number;
  paginationParams: PaginationParams;
  productFeatureIsDisabled: boolean;
  roles: Role[];
  destroyRole: (roleId: string) => Promise<void>;
}

const DashboardRolesView = ({
  currentUser,
  environments,
  filters,
  handleCreateRole,
  isLoading,
  onOmniSearch,
  onPageLinkClick,
  onPageSizeClick,
  pageSize,
  paginationParams,
  productFeatureIsDisabled,
  roles,
  destroyRole,
}: DashboardRolesViewProps) => {
  const { omni } = filters;
  const [environmentsFilter, setEnvironmentsFilter] = useState<string[]>([]);
  const navigate = useNavigate();

  const [createRoleModal, { enable: showCreateRoleModal, disable: hideCreateRoleModal }] =
    useToggle(false);

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [hoveredRoleId, setHoveredRoleId] = useState<string>(null);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [roleIdToDeactivate, setRoleIdToDeactivate] = useState<string>(null);

  const [
    deactivateRoleModal,
    { enable: showDeactivateRoleModal, disable: hideDeactivateRoleModal },
  ] = useToggle(false);

  const handleCreateRoleAndClose = async (values: FormCreateRoleData) => {
    await handleCreateRole(values);
    hideCreateRoleModal();
  };

  const roleIdsToListOfEnvironmentIds = useMemo(() => {
    return roles.reduce(
      (result, role) => {
        result[role.id] = uniq(
          role.attributes['role-permissions']
            .map((rp) => rp['environment-id'])
            .filter((e) => e != null),
        );

        return result;
      },
      {} as Record<string, string[]>,
    );
  }, [roles]);

  const visibleRoles = useMemo(() => {
    return roles.filter((r) => {
      const roleEnvironments = roleIdsToListOfEnvironmentIds[r.id];

      return (
        environmentsFilter.length === 0 ||
        intersection(environmentsFilter, roleEnvironments).length > 0 ||
        r.attributes['role-permissions'].every((rp) => rp['environment-id'] == null)
      );
    });
  }, [roles, environmentsFilter, roleIdsToListOfEnvironmentIds]);

  const canEditRole = currentUser.attributes['is-admin'] && !productFeatureIsDisabled;

  const filterConfig: FilterConfig[] = [
    {
      id: 'environment',
      name: 'Environment',
      type: 'multiselect',
      options: environments.map((e) => {
        return {
          label: e.attributes.name,
          value: e.id,
        };
      }),
      value: environmentsFilter,
      onChange: setEnvironmentsFilter,
      placeholder: 'Select environment',
    },
  ];

  return (
    <>
      <Header title='Roles' />
      <Container>
        <Section>
          <Card>
            <Card.Body>
              <CardTitleContainer data-test='dashboard-user-roles__header'>
                <Card.Title>
                  Roles
                  {environments.length > 0 && productFeatureIsDisabled && (
                    <LimitedStickerContainer data-marketing-id='roles-limited-badge'>
                      <Sticker variant='teal' icon={<StarFillIcon color={Color.PrimaryBlack} />}>
                        Limited
                      </Sticker>
                    </LimitedStickerContainer>
                  )}
                </Card.Title>
                {canEditRole && (
                  <Button
                    id='roles-table__button--create-role'
                    name='roles-table__button--create-role'
                    size='sm'
                    width='auto'
                    data-test='roles-table__button--create-role'
                    onClick={showCreateRoleModal}
                    disabled={!canEditRole}
                  >
                    Create role
                  </Button>
                )}
              </CardTitleContainer>
              {isLoading ? (
                <LoadingIndicator />
              ) : (
                <>
                  <TableControls
                    searchProps={{
                      id: 'roles-table__search-input',
                      handleInput: onOmniSearch,
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      value: omni,
                      placeholder: 'Search by role name',
                    }}
                    filters={filterConfig}
                  />
                  <Table data-test='dashboard-user-roles__table'>
                    <TableRow>
                      <StyledTableHeaderCell>Name</StyledTableHeaderCell>
                      <StyledTableHeaderCell>Description</StyledTableHeaderCell>
                      <StyledTableHeaderCell>Environments</StyledTableHeaderCell>
                      <StyledTableHeaderCell>{`Created at (${DateTime.local().toFormat(
                        TIMEZONE_NAME_ABBREVIATED,
                      )})`}</StyledTableHeaderCell>
                      <StyledTableHeaderCell>{/* Deactivate */}</StyledTableHeaderCell>
                    </TableRow>
                    <TableBody>
                      {visibleRoles.map((role) => (
                        <StyledTableRow
                          id={`roles-table__row--${role.id}`}
                          key={`roles-table__row--${role.id}`}
                          data-test={`roles-table__row--${role.id}`}
                          onClick={() => navigate(`/dashboard/user-roles/${role.id}`)}
                          onMouseOver={(e: MouseEvent<HTMLDivElement>) => {
                            e.stopPropagation();
                            setHoveredRoleId(role.id);
                          }}
                          onMouseLeave={(e: MouseEvent<HTMLDivElement>) => {
                            e.stopPropagation();
                            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                            setHoveredRoleId(null);
                          }}
                        >
                          <StyledTableCell>{role.attributes.name}</StyledTableCell>
                          <StyledTableCell>{role.attributes.description}</StyledTableCell>
                          <StyledEnvironmentsTableCell>
                            {role.attributes.name === ADMIN_ROLE_NAME ||
                            some(
                              (rp) =>
                                rp.name === Permission.EnvironmentView &&
                                rp['environment-id'] == null,
                              role.attributes['role-permissions'],
                            ) ||
                            (currentUser.attributes['is-admin'] &&
                              roleIdsToListOfEnvironmentIds[role.id].length ===
                                environments.length) ? (
                              <>
                                <Sticker>All</Sticker>
                              </>
                            ) : (
                              roleIdsToListOfEnvironmentIds[role.id].map((environmentId) => {
                                const environmentName = environments.find(
                                  (e) => e.id === environmentId,
                                )?.attributes?.name;

                                return environmentName != null ? (
                                  <Sticker
                                    key={`sticker-${role.id}-${environmentId}`}
                                    variant='primary'
                                  >
                                    {environmentName}
                                  </Sticker>
                                ) : (
                                  <Sticker key={`sticker-${role.id}-${environmentId}`}>
                                    Restricted
                                  </Sticker>
                                );
                              })
                            )}
                          </StyledEnvironmentsTableCell>
                          <StyledTableCell>
                            {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                            {DateTime.fromISO(role.attributes['created-at']).toFormat(
                              DATE_AND_TIME_FORMAT,
                            )}
                          </StyledTableCell>
                          <StyledButtonTableCell>
                            {hoveredRoleId === role.id &&
                              role.relationships.users.data.length === 0 && (
                                <Button
                                  id={`roles-table__row-deactivate--${role.id}`}
                                  size='sm'
                                  variant='basic'
                                  width='auto'
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setRoleIdToDeactivate(role.id);
                                    showDeactivateRoleModal();
                                  }}
                                >
                                  Deactivate
                                </Button>
                              )}
                          </StyledButtonTableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Pagination
                    pageSize={pageSize}
                    paginationParams={paginationParams}
                    handlePageSizeClick={onPageSizeClick}
                    handlePageLinkClick={onPageLinkClick}
                  />
                </>
              )}
            </Card.Body>
          </Card>
          <ModalWrapper isOpen={createRoleModal} handleCloseModal={hideCreateRoleModal}>
            <ModalContainer data-test='create-role-modal'>
              <ModalHeader data-test='create-role-modal__header' onClose={hideCreateRoleModal}>
                Create a New Role
              </ModalHeader>

              <ModalBody>
                <FormCreateRole
                  closeModal={hideCreateRoleModal}
                  onSubmit={handleCreateRoleAndClose}
                />
              </ModalBody>
            </ModalContainer>
          </ModalWrapper>
          <ModalWrapper isOpen={deactivateRoleModal} handleCloseModal={hideDeactivateRoleModal}>
            <ModalContainer data-test='deactivate-role-modal'>
              <ModalHeader
                data-test='deactivate-role-modal__header'
                onClose={hideDeactivateRoleModal}
              >
                {`Deactivate ${roles.find((r) => r.id === roleIdToDeactivate)?.attributes?.name}?`}
              </ModalHeader>

              <ModalBody>
                <HStack equal={true}>
                  <Button
                    id='deactivate-role-form__close'
                    variant='basic'
                    onClick={hideDeactivateRoleModal}
                  >
                    Cancel
                  </Button>
                  <Button
                    id='deactivate-role-form__submit'
                    type='submit'
                    onClick={() => {
                      destroyRole(roleIdToDeactivate);
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      setRoleIdToDeactivate(null);
                      hideDeactivateRoleModal();
                    }}
                  >
                    Deactivate
                  </Button>
                </HStack>
              </ModalBody>
            </ModalContainer>
          </ModalWrapper>
        </Section>
      </Container>
    </>
  );
};

export default DashboardRolesView;

const CardTitleContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const StyledTableHeaderCell = styled(TableHeaderCell)`
  height: 44px;
`;

const StyledTableCell = styled(TableCell)`
  padding: 0 12px 0 12px;
  height: 44px;
`;

const StyledButtonTableCell = styled(StyledTableCell)`
  width: 100px;
`;

const StyledEnvironmentsTableCell = styled(TableCell)`
  padding: 12px;
  gap: ${Spacing[2]} ${Spacing[1]};
  justify-content: flex-start;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  min-height: 44px;
`;

const StyledTableRow = styled(TableRow)`
  cursor: pointer;
`;

const LimitedStickerContainer = styled.div`
  display: flex;
  margin-top: auto;
  margin-bottom: auto;
  cursor: default;
`;
